import Button from "@components/Button"
import Layout from "@components/Layout"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

function ChatBubble({
  text,
  isUser,
  delay,
}: {
  text: string
  isUser: boolean
  delay: number
}) {
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      style={{
        animation: `fadeSlideUp 0.4s ease-out ${delay}s both`,
      }}
    >
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
          isUser
            ? "rounded-br-sm bg-[#dcf8c6] text-gray-800"
            : "rounded-bl-sm bg-white text-gray-800 shadow-sm ring-1 ring-gray-100"
        }`}
      >
        {text}
      </div>
    </div>
  )
}

function PhonePreview() {
  const { t } = useTranslation()

  return (
    <div className="w-full max-w-xs overflow-hidden rounded-3xl border border-gray-200 bg-[#ece5dd] shadow-lg">
      {/* WhatsApp header */}
      <div className="flex items-center gap-3 bg-[#075e54] px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <span className="text-sm font-bold text-white">C</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">CalCount AI</p>
          <p className="text-[10px] text-white/70">
            {t("pages.onboarding.whatsAppTracking.online")}
          </p>
        </div>
      </div>

      {/* Chat area */}
      <div className="space-y-2 px-3 py-4">
        <ChatBubble
          text={t("pages.onboarding.whatsAppTracking.chat.user1")}
          isUser={true}
          delay={0.3}
        />
        <ChatBubble
          text={t("pages.onboarding.whatsAppTracking.chat.bot1")}
          isUser={false}
          delay={0.9}
        />
        <ChatBubble
          text={t("pages.onboarding.whatsAppTracking.chat.user2")}
          isUser={true}
          delay={1.5}
        />
        <ChatBubble
          text={t("pages.onboarding.whatsAppTracking.chat.bot2")}
          isUser={false}
          delay={2.1}
        />
      </div>
    </div>
  )
}

export default function WhatsAppTracking() {
  const { t } = useTranslation()
  const submit = useSubmit()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  return (
    <Layout>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="flex grow flex-col items-center justify-center px-2">
        <h1 className="custom-heading mb-2">
          {t("pages.onboarding.whatsAppTracking.heading")}
        </h1>
        <p className="mb-6 text-center text-sm leading-relaxed text-gray-500">
          {t("pages.onboarding.whatsAppTracking.info")}
        </p>

        <PhonePreview />

        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {["text", "photo", "voice"].map((method) => (
            <span
              key={method}
              className="bg-custom-teal-light text-custom-teal-dark inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
            >
              <span>
                {method === "text" ? "💬" : method === "photo" ? "📸" : "🎙️"}
              </span>
              {t(`pages.onboarding.whatsAppTracking.methods.${method}`)}
            </span>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
        <Form method="post" onSubmit={handleSubmit}>
          <input name="whatsAppTracking" type="hidden" value="continue" />
          <Button title={t("shared.continue")} type="submit" />
        </Form>
      </div>
    </Layout>
  )
}
